import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboard';
import { AnalyticsData, GlobalInsectRecordsResponse, Hotspot, PredictionResponse } from '../../types';
import RiskIndicators from './RiskIndicators';
import apisMelliferaImage from '../../assets/images/species/apis-mellifera.png';
import bombusTerrestrisImage from '../../assets/images/species/bombus-terrestris.png';
import bombusImpatiensImage from '../../assets/images/species/Bombus-mpatiens.png';
import danausPlexippusImage from '../../assets/images/species/Danaus-plexippus.png';
import vanessaCarduiImage from '../../assets/images/species/Vanessa-cardui.png';
import pierisRapaeImage from '../../assets/images/species/pieris-rapae.png';
import papilioDemoleusImage from '../../assets/images/species/papilio-demoleus.jpg';

const REGION_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  Asia: { latitude: 19.076, longitude: 72.8777 },
  Europe: { latitude: 48.8566, longitude: 2.3522 },
  'North America': { latitude: 40.7128, longitude: -74.006 },
  Africa: { latitude: -1.2864, longitude: 36.8172 },
};

const DASHBOARD_PRIMARY_REGIONS = ['North America', 'Asia', 'Europe', 'Africa'];

const SPECIES_OPTIONS = [
  'Apis mellifera',
  'Bombus terrestris',
  'Bombus impatiens',
  'Danaus plexippus',
  'Vanessa cardui',
  'Pieris rapae',
  'Papilio demoleus',
];

const CROP_OPTIONS = [
  'Rice',
  'Wheat',
  'Maize',
  'Cotton',
  'Soybean',
  'Sugarcane',
  'Vegetables',
  'Pulses',
];

const SPECIES_IMAGE_MAP: Record<string, string> = {
  'Apis mellifera': apisMelliferaImage,
  'Bombus terrestris': bombusTerrestrisImage,
  'Bombus impatiens': bombusImpatiensImage,
  'Danaus plexippus': danausPlexippusImage,
  'Vanessa cardui': vanessaCarduiImage,
  'Pieris rapae': pierisRapaeImage,
  'Papilio demoleus': papilioDemoleusImage,
};

const getSpeciesImageAlt = (speciesName: string): string => {
  return `${speciesName} insect species image for biodiversity risk prediction, habitat monitoring, and conservation analytics`;
};

const pickFirst = (source: Record<string, any>, keys: string[], fallback: any = undefined) => {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return fallback;
};

const toNumber = (value: any, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCompactCount = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '0';
  }
  if (Math.abs(value) >= 1_000_000) {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  return new Intl.NumberFormat('en-US').format(value);
};

const splitObservedSeries = (recordsByYear: Array<{ year: number; count: number }>) => {
  const ordered = [...recordsByYear].sort((left, right) => left.year - right.year);
  if (ordered.length < 3) {
    return {
      observed: ordered,
      excludedPartialYear: null as number | null,
    };
  }

  const currentYear = new Date().getUTCFullYear();
  const last = ordered[ordered.length - 1];
  if (last.year !== currentYear) {
    return {
      observed: ordered,
      excludedPartialYear: null as number | null,
    };
  }

  const priorWindow = ordered.slice(-6, -1).map((item) => item.count);
  if (priorWindow.length < 3) {
    return {
      observed: ordered,
      excludedPartialYear: null as number | null,
    };
  }

  const sortedWindow = [...priorWindow].sort((a, b) => a - b);
  const median = sortedWindow[Math.floor(sortedWindow.length / 2)];
  const isPartialTrailingYear = median > 0 && last.count < median * 0.5;

  if (!isPartialTrailingYear) {
    return {
      observed: ordered,
      excludedPartialYear: null as number | null,
    };
  }

  return {
    observed: ordered.slice(0, -1),
    excludedPartialYear: last.year,
  };
};

const buildYearTicks = (years: number[], forcedYears: number[] = []): number[] => {
  const uniqueYears = Array.from(new Set(years.filter((year) => Number.isFinite(year)))).sort((left, right) => left - right);
  if (uniqueYears.length <= 14) {
    return uniqueYears;
  }

  const step = Math.ceil((uniqueYears.length - 1) / 12);
  const sampled: number[] = [];
  for (let index = 0; index < uniqueYears.length; index += step) {
    sampled.push(uniqueYears[index]);
  }
  sampled.push(uniqueYears[uniqueYears.length - 1]);

  const merged = Array.from(new Set([...sampled, ...forcedYears])).sort((left, right) => left - right);
  return merged;
};

type FeedbackOutcome = 'pending' | 'improved' | 'no_change' | 'worse';

interface NormalizedGlobalRecords {
  totalSampledRecords: number;
  uniqueSpecies: number;
  countryCoverage: number;
  isFallback: boolean;
  queryLimit: number;
  recordsByYear: Array<{ year: number; count: number }>;
  forecastByYear: Array<{ year: number; count: number }>;
  forecastModel?: {
    name: string;
    alpha: number;
    beta: number;
    horizon_years: number;
    trend_per_year: number;
    last_observed_year: number | null;
    last_observed_count: number | null;
  };
  topSpecies: Array<{ species: string; count: number }>;
  topCountries: Array<{ country: string; count: number }>;
  recentAlerts: string[];
  nasa: {
    avgTemperatureCelsius: number;
    avgHumidityPercent: number;
    totalPrecipitationMm: number;
    soilMoistureIndex: number;
    vegetationIndex: number;
    sampledRegions: number;
  };
}

const normalizeGlobalRecords = (payload: any): NormalizedGlobalRecords => {
  const src = (payload ?? {}) as Record<string, any>;
  const query = (pickFirst(src, ['query'], {}) ?? {}) as Record<string, any>;
  const nasaSummary = (pickFirst(src, ['nasa_summary', 'nasaSummary'], {}) ?? {}) as Record<string, any>;

  const normalizedYearPoints = (pickFirst(src, ['records_by_year', 'recordsByYear'], []) ?? [])
    .map((item: any) => ({
      year: toNumber(pickFirst(item ?? {}, ['year']), 0),
      count: toNumber(pickFirst(item ?? {}, ['count', 'records', 'value']), 0),
    }))
    .filter((item: { year: number; count: number }) => item.year > 0);

  const normalizedTopSpecies = (pickFirst(src, ['top_species', 'topSpecies'], []) ?? []).map((item: any) => ({
    species: String(pickFirst(item ?? {}, ['species', 'name', 'label'], 'Unknown')),
    count: toNumber(pickFirst(item ?? {}, ['count', 'records', 'value']), 0),
  }));

  const normalizedTopCountries = (pickFirst(src, ['top_countries', 'topCountries'], []) ?? []).map((item: any) => ({
    country: String(pickFirst(item ?? {}, ['country', 'name', 'label'], 'Unknown')),
    count: toNumber(pickFirst(item ?? {}, ['count', 'records', 'value']), 0),
  }));
  const forecastByYear = (pickFirst(src, ['forecast_by_year', 'forecastByYear'], []) ?? []).map((item: any) => ({
    year: toNumber(pickFirst(item ?? {}, ['year']), 0),
    count: toNumber(pickFirst(item ?? {}, ['count', 'records', 'value']), 0),
  }));

  return {
    totalSampledRecords: toNumber(pickFirst(src, ['total_sampled_records', 'totalSampledRecords', 'sampled_records']), 0),
    uniqueSpecies: toNumber(pickFirst(src, ['unique_species', 'uniqueSpecies', 'species_count']), 0),
    countryCoverage: toNumber(pickFirst(src, ['country_coverage', 'countryCoverage', 'mapped_country_coverage']), 0),
    isFallback: Boolean(pickFirst(src, ['is_fallback', 'isFallback'], false)),
    queryLimit: toNumber(pickFirst(query, ['limit'], 0), 0),
    recordsByYear: normalizedYearPoints,
    forecastByYear,
    forecastModel: pickFirst(src, ['forecast_model', 'forecastModel'], undefined),
    topSpecies: normalizedTopSpecies,
    topCountries: normalizedTopCountries,
    recentAlerts: (pickFirst(src, ['recent_alerts', 'recentAlerts'], []) ?? []) as string[],
    nasa: {
      avgTemperatureCelsius: toNumber(
        pickFirst(nasaSummary, ['avg_temperature_celsius', 'avgTemperatureCelsius', 'avg_temp_c']),
        0,
      ),
      avgHumidityPercent: toNumber(
        pickFirst(nasaSummary, ['avg_humidity_percent', 'avgHumidityPercent', 'avg_humidity']),
        0,
      ),
      totalPrecipitationMm: toNumber(
        pickFirst(nasaSummary, ['total_precipitation_mm', 'totalPrecipitationMm', 'precipitation_mm']),
        0,
      ),
      soilMoistureIndex: toNumber(
        pickFirst(nasaSummary, ['soil_moisture_index', 'soilMoistureIndex', 'soil_moisture']),
        0,
      ),
      vegetationIndex: toNumber(
        pickFirst(nasaSummary, ['vegetation_index', 'vegetationIndex', 'ndvi']),
        0,
      ),
      sampledRegions: toNumber(pickFirst(nasaSummary, ['sampled_regions', 'sampledRegions']), 0),
    },
  };
};

const DashboardPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [region, setRegion] = useState('Asia');
  const [species, setSpecies] = useState('Danaus plexippus');
  const [crop, setCrop] = useState('Rice');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [lastPrediction, setLastPrediction] = useState<PredictionResponse | null>(null);
  const [globalRecords, setGlobalRecords] = useState<GlobalInsectRecordsResponse | null>(null);
  const [globalRecordsError, setGlobalRecordsError] = useState('');
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<string, { outcome: FeedbackOutcome; note: string; saving: boolean; status: string }>>({});
  const [error, setError] = useState('');
  const [loadingPrediction, setLoadingPrediction] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingGlobalRecords, setLoadingGlobalRecords] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [regionalRiskData, setRegionalRiskData] = useState<any>(null);
  const normalizedGlobal = globalRecords ? normalizeGlobalRecords(globalRecords) : null;
  const observedSeries = normalizedGlobal ? splitObservedSeries(normalizedGlobal.recordsByYear) : { observed: [], excludedPartialYear: null as number | null };
  const partialObservedPoint =
    normalizedGlobal && observedSeries.excludedPartialYear
      ? normalizedGlobal.recordsByYear.find((item) => item.year === observedSeries.excludedPartialYear)
      : undefined;

  const loadDashboardData = useCallback(async () => {
    if (!isAuthenticated) {
      setAnalytics(null);
      setHotspots([]);
      setLastRefreshedAt('');
      setLoadingDashboard(false);
      return;
    }

    setLoadingDashboard(true);
    try {
      const [analyticsData, hotspotsData] = await Promise.all([
        dashboardService.getAnalytics(),
        dashboardService.getHotspots(),
      ]);
      setAnalytics(analyticsData);
      setHotspots(hotspotsData.hotspots);
      setLastRefreshedAt(new Date().toISOString());
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load analytics.');
    } finally {
      setLoadingDashboard(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const loadGlobalRecords = useCallback(async () => {
    setLoadingGlobalRecords(true);
    setGlobalRecordsError('');
    try {
      const currentYear = new Date().getUTCFullYear();
      const response = await dashboardService.getGlobalInsectRecordsForecast(1990, currentYear, 350, 5);
      setGlobalRecords(response);
    } catch (err: any) {
      // Keep last successful feed visible if refresh fails.
      const detail = err?.response?.data?.detail || err?.message || 'Unable to load global records right now.';
      setGlobalRecordsError(detail);
    } finally {
      setLoadingGlobalRecords(false);
    }
  }, []);

  useEffect(() => {
    loadGlobalRecords();
  }, [loadGlobalRecords]);

  const trendChartData = normalizedGlobal
    ? [
        ...observedSeries.observed.map((item) => ({
          year: item.year,
          count: item.count,
          partialCount: null,
          forecastCount: null,
        })),
        ...(partialObservedPoint
          ? [
              {
                year: partialObservedPoint.year,
                count: null,
                partialCount: partialObservedPoint.count,
                forecastCount: null,
              },
            ]
          : []),
        ...normalizedGlobal.forecastByYear.map((item) => ({
          year: item.year,
          count: null,
          partialCount: null,
          forecastCount: item.count,
        })),
      ]
    : [];
  const trendChartTicks = buildYearTicks(
    trendChartData.map((item) => item.year),
    observedSeries.excludedPartialYear ? [observedSeries.excludedPartialYear] : [],
  );
  const highlightedSpecies = lastPrediction?.species || species;
  const highlightedImage = SPECIES_IMAGE_MAP[highlightedSpecies] || danausPlexippusImage;
  const confidencePercent = Math.max(0, Math.min(100, Number((((lastPrediction?.confidence ?? 0) * 100).toFixed(1)))));
  const suitabilityPercent = Math.max(
    0,
    Math.min(100, Number((lastPrediction?.decision_support.crop_suitability_percent ?? 0).toFixed(1))),
  );
  const yieldDeltaPercent = Number((lastPrediction?.decision_support.expected_yield_delta_percent ?? 0).toFixed(1));
  const normalizedYieldMeter = Math.max(0, Math.min(100, ((yieldDeltaPercent + 20) / 40) * 100));

  // Load regional risk data (no auth needed)
  const loadRegionalRiskData = useCallback(async () => {
    try {
      const response = await dashboardService.getRegionalLiveRisk(2021, 2026, undefined, 300);
      setRegionalRiskData(response);
    } catch (err: any) {
      console.error('Failed to load regional risk:', err);
      setRegionalRiskData(null);
    }
  }, []);

  useEffect(() => {
    loadRegionalRiskData();
  }, [loadRegionalRiskData]);

  const handlePredict = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isAuthenticated) {
      setError('Sign in to generate predictions and sync dashboard analytics.');
      return;
    }

    setError('');
    setLoadingPrediction(true);

    if (!region.trim()) {
      setError('Please choose a region.');
      setLoadingPrediction(false);
      return;
    }

    if (!species.trim()) {
      setError('Please choose a species.');
      setLoadingPrediction(false);
      return;
    }

    const coordinates = REGION_COORDINATES[region];
    if (!coordinates) {
      setError('Please choose a valid region.');
      setLoadingPrediction(false);
      return;
    }

    const lat = coordinates.latitude;
    const lon = coordinates.longitude;

    try {
      const prediction = await dashboardService.predict({
        region,
        species,
        crop,
        latitude: lat,
        longitude: lon,
      });
      setLastPrediction(prediction);
      const nextDrafts: Record<string, { outcome: FeedbackOutcome; note: string; saving: boolean; status: string }> = {};
      prediction.action_cards.forEach((card) => {
        nextDrafts[card.card_id] = {
          outcome: card.outcome || 'pending',
          note: card.note || '',
          saving: false,
          status: '',
        };
      });
      setFeedbackDrafts(nextDrafts);
      await loadDashboardData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Prediction request failed.');
    } finally {
      setLoadingPrediction(false);
    }
  };

  const updateFeedbackDraft = (cardId: string, patch: Partial<{ outcome: FeedbackOutcome; note: string; saving: boolean; status: string }>) => {
    setFeedbackDrafts((prev) => ({
      ...prev,
      [cardId]: {
        outcome: prev[cardId]?.outcome || 'pending',
        note: prev[cardId]?.note || '',
        saving: false,
        status: prev[cardId]?.status || '',
        ...patch,
      },
    }));
  };

  const submitCardFeedback = async (cardId: string, accepted: boolean) => {
    if (!lastPrediction) {
      return;
    }

    const draft = feedbackDrafts[cardId] || { outcome: 'pending' as FeedbackOutcome, note: '', saving: false, status: '' };
    updateFeedbackDraft(cardId, { saving: true, status: '' });

    try {
      await dashboardService.submitRecommendationFeedback(lastPrediction.prediction_id, {
        card_id: cardId,
        accepted,
        outcome: draft.outcome,
        note: draft.note,
      });

      setLastPrediction((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          action_cards: prev.action_cards.map((card) =>
            card.card_id === cardId
              ? {
                  ...card,
                  accepted,
                  outcome: draft.outcome,
                  note: draft.note,
                  last_feedback_at: new Date().toISOString(),
                }
              : card,
          ),
        };
      });

      updateFeedbackDraft(cardId, { saving: false, status: accepted ? 'Marked as applied' : 'Marked as not applied' });
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Unable to save recommendation feedback.';
      updateFeedbackDraft(cardId, { saving: false, status: message });
    }
  };

  return (
    <section className="feature-page dashboard-premium">
      <div className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <p className="dashboard-kicker">EcoPredict Command Center</p>
          <h1>Dashboard</h1>
          <p>Generate biodiversity predictions and monitor recent risk levels.</p>
        </div>

        <div className="dashboard-hero-metrics" aria-label="Dashboard snapshot">
          <article className="dashboard-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◌</span> Total Predictions</h3>
            <p>{analytics?.total_predictions ?? 0}</p>
          </article>
          <article className="dashboard-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◍</span> Avg Confidence</h3>
            <p>{((analytics?.average_confidence ?? 0) * 100).toFixed(1)}%</p>
          </article>
          <article className="dashboard-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◈</span> Top Risk Region</h3>
            <p>{regionalRiskData?.regions?.[0]?.region ?? 'Loading...'}</p>
          </article>
          <article className="dashboard-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◎</span> Hotspots Tracked</h3>
            <p>{hotspots.length}</p>
          </article>
        </div>
      </div>

      {regionalRiskData && (
        <section className="panel live-risk-summary">
          <h2>Live Regional Biodiversity Risk</h2>
          <p>Real-time risk assessment powered by GBIF occurrence data and NASA climate signals.</p>
          {regionalRiskData.regions && regionalRiskData.regions.length > 0 && (
            <div className="regional-risk-grid-summary">
              {regionalRiskData.regions
                .filter((region: any) => DASHBOARD_PRIMARY_REGIONS.includes(region.region))
                .map((region: any) => (
                <div key={region.region} className={`live-risk-card risk-${region.risk_level.toLowerCase()}`}>
                  <h3>{region.region}</h3>
                  <p className="risk-score">{region.risk_score.toFixed(1)}</p>
                  <p className="risk-level">{region.risk_level}</p>
                  <p className="risk-detail">Records: {region.record_count}</p>
                  <p className="risk-detail">Species: {region.dominant_species}</p>
                  <p className="risk-detail">Temp: {region.avg_temperature_celsius.toFixed(1)}°C</p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {!isAuthenticated && (
        <div className="panel guest-cta-panel">
          <h2>Explore As Guest</h2>
          <p>
            You are viewing the public dashboard preview. To run predictions and load your personalized analytics,
            please sign in or create an account.
          </p>
          <div className="guest-cta-actions">
            <Link to="/login" className="auth-link-btn auth-link-btn-ghost">
              Sign in
            </Link>
            <Link to="/signup" className="auth-link-btn">
              Sign up
            </Link>
          </div>
        </div>
      )}

      <div className="dashboard-toolbar">
        <button
          type="button"
          onClick={loadDashboardData}
          disabled={!isAuthenticated || loadingDashboard || loadingPrediction}
        >
          {loadingDashboard ? 'Refreshing...' : 'Refresh Dashboard'}
        </button>
        <p className="dashboard-last-updated">
          Last updated: {lastRefreshedAt ? new Date(lastRefreshedAt).toLocaleString() : 'Not loaded yet'}
        </p>
      </div>

      <section className="dashboard-grid">
        <div className="panel prediction-workbench-panel">
          <h2>Generate Prediction</h2>
          <form className="predict-form" onSubmit={handlePredict}>
            <label htmlFor="region">Region</label>
            <select
              id="region"
              value={region}
              onChange={(event) => {
                setRegion(event.target.value);
              }}
              required
            >
              {Object.keys(REGION_COORDINATES).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <label htmlFor="species">Species</label>
            <select
              id="species"
              value={species}
              onChange={(event) => setSpecies(event.target.value)}
              required
            >
              {SPECIES_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <label htmlFor="crop">Crop</label>
            <select
              id="crop"
              value={crop}
              onChange={(event) => setCrop(event.target.value)}
              required
            >
              {CROP_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <p className="analytics-summary region-coord-note">
              Coordinates are auto-selected from region: {REGION_COORDINATES[region].latitude},{' '}
              {REGION_COORDINATES[region].longitude}
            </p>

            <button type="submit" disabled={loadingPrediction || loadingDashboard}>
              {loadingPrediction ? 'Predicting...' : 'Predict Biodiversity Risk'}
            </button>
          </form>

          {lastPrediction && (
            <div className="prediction-result">
              <p className="prediction-kicker">AI Farm Command Insight</p>
              <h3 className="prediction-title">Latest Prediction</h3>
              <div className="prediction-meta-grid">
                <article>
                  <span>Region</span>
                  <strong>{lastPrediction.region}</strong>
                </article>
                <article>
                  <span>Species</span>
                  <strong>{lastPrediction.species}</strong>
                </article>
                <article>
                  <span>Crop</span>
                  <strong>{lastPrediction.crop || crop}</strong>
                </article>
                <article>
                  <span>Risk Level</span>
                  <strong className={`prediction-risk-pill prediction-risk-${lastPrediction.risk_level.toLowerCase()}`}>
                    {lastPrediction.risk_level}
                  </strong>
                </article>
                <article>
                  <span>Confidence</span>
                  <strong>{(lastPrediction.confidence * 100).toFixed(1)}%</strong>
                </article>
              </div>
              <p className={`prediction-alert prediction-alert-${lastPrediction.alert_level.toLowerCase()}`}>
                {lastPrediction.alert_level}: {lastPrediction.alert_message}
              </p>

              <div className="prediction-meters">
                <article className="prediction-meter-card">
                  <div className="prediction-meter-head">
                    <span>Confidence</span>
                    <strong>{confidencePercent.toFixed(1)}%</strong>
                  </div>
                  <div className="prediction-meter-track">
                    <div className="prediction-meter-fill meter-confidence" style={{ width: `${confidencePercent}%` }} />
                  </div>
                </article>

                <article className="prediction-meter-card">
                  <div className="prediction-meter-head">
                    <span>Crop Suitability</span>
                    <strong>{suitabilityPercent.toFixed(1)}%</strong>
                  </div>
                  <div className="prediction-meter-track">
                    <div className="prediction-meter-fill meter-suitability" style={{ width: `${suitabilityPercent}%` }} />
                  </div>
                </article>

                <article className="prediction-meter-card">
                  <div className="prediction-meter-head">
                    <span>Yield Shift</span>
                    <strong>{yieldDeltaPercent >= 0 ? '+' : ''}{yieldDeltaPercent.toFixed(1)}%</strong>
                  </div>
                  <div className="prediction-meter-track">
                    <div className="prediction-meter-fill meter-yield" style={{ width: `${normalizedYieldMeter}%` }} />
                  </div>
                </article>
              </div>

              <div className="prediction-actions">
                <h4>Recommended Actions</h4>
                <ul>
                  {lastPrediction.action_cards.map((card) => (
                    <li key={card.card_id}>
                      <div className="action-card-head">
                        <strong>{card.title}</strong>
                        <span className="action-card-timing">{card.timing}</span>
                      </div>
                      <p className="action-card-guidance">{card.guidance}</p>
                      <div className="recommendation-feedback-controls">
                        <label htmlFor={`outcome-${card.card_id}`}>Outcome</label>
                        <select
                          id={`outcome-${card.card_id}`}
                          value={feedbackDrafts[card.card_id]?.outcome || card.outcome || 'pending'}
                          onChange={(event) => updateFeedbackDraft(card.card_id, { outcome: event.target.value as FeedbackOutcome })}
                        >
                          <option value="pending">Pending</option>
                          <option value="improved">Improved</option>
                          <option value="no_change">No Change</option>
                          <option value="worse">Worse</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Outcome note (optional)"
                          value={feedbackDrafts[card.card_id]?.note || ''}
                          onChange={(event) => updateFeedbackDraft(card.card_id, { note: event.target.value })}
                        />
                        <div className="recommendation-feedback-buttons">
                          <button
                            type="button"
                            onClick={() => submitCardFeedback(card.card_id, true)}
                            disabled={feedbackDrafts[card.card_id]?.saving}
                          >
                            {feedbackDrafts[card.card_id]?.saving ? 'Saving...' : 'Mark Applied'}
                          </button>
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={() => submitCardFeedback(card.card_id, false)}
                            disabled={feedbackDrafts[card.card_id]?.saving}
                          >
                            Not Applied
                          </button>
                        </div>
                        {card.accepted !== undefined && card.accepted !== null && (
                          <p className="recommendation-status">
                            Status: {card.accepted ? 'Applied' : 'Not Applied'} | Outcome: {card.outcome || 'pending'}
                          </p>
                        )}
                        {feedbackDrafts[card.card_id]?.status && (
                          <p className="recommendation-status">{feedbackDrafts[card.card_id]?.status}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="decision-support-panel">
                <h4>Farm Decision Support</h4>
                <p>
                  Crop Suitability: <strong>{lastPrediction.decision_support.crop_suitability_percent.toFixed(1)}%</strong>
                  {' '}({lastPrediction.decision_support.confidence_band} confidence)
                </p>
                <p>
                  Expected Yield Shift: <strong>{lastPrediction.decision_support.expected_yield_delta_percent.toFixed(1)}%</strong>
                  {' '}| Calibration: {lastPrediction.decision_support.calibration_adjustment_percent >= 0 ? '+' : ''}
                  {lastPrediction.decision_support.calibration_adjustment_percent.toFixed(1)} points
                  {' '}from {lastPrediction.decision_support.calibration_samples} feedback samples
                </p>
                <p>{lastPrediction.decision_support.calibration_summary}</p>
                <p>{lastPrediction.decision_support.crop_decision}</p>

                <div className="decision-support-grid">
                  <article>
                    <h5>Weather Windows</h5>
                    <ul>
                      <li>Irrigation: {lastPrediction.decision_support.weather_window.irrigation_window}</li>
                      <li>Spray: {lastPrediction.decision_support.weather_window.spray_window}</li>
                      <li>Field Work: {lastPrediction.decision_support.weather_window.field_work_window}</li>
                      <li>Caution: {lastPrediction.decision_support.weather_window.caution}</li>
                    </ul>
                  </article>

                  <article>
                    <h5>Pollinator Species Plan</h5>
                    <ul>
                      {lastPrediction.decision_support.pollinator_species.map((item) => (
                        <li key={item.species_name}>
                          {item.species_name}: {item.suitability_percent.toFixed(1)}% - {item.why}
                        </li>
                      ))}
                    </ul>
                  </article>

                  <article>
                    <h5>Honeybee Habitat Plan</h5>
                    <ul>
                      {lastPrediction.decision_support.honeybee_habitat_plan.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ul>
                  </article>
                </div>

                <div className="decision-factors">
                  <h5>Key Environmental Factors</h5>
                  <ul>
                    {lastPrediction.decision_support.key_environment_factors.map((factor) => (
                      <li key={factor}>{factor}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="prediction-timestamp">Timestamp: {new Date(lastPrediction.timestamp).toLocaleString()}</p>
            </div>
          )}
        </div>

        <div className="panel dashboard-side-panel risk-overview-panel">
          <h2>Risk Overview</h2>
          <RiskIndicators analytics={analytics} />
          <p className="analytics-summary">
            Total Predictions: {analytics?.total_predictions ?? 0}
            <br />
            Avg Confidence: {((analytics?.average_confidence ?? 0) * 100).toFixed(1)}%
          </p>
        </div>

        <div className="panel prediction-photo-panel dashboard-side-panel">
          <h2>Prediction Photo Spotlight</h2>
          <p className="analytics-summary">
            Species visuals now appear here when you predict, replacing the hotspot ID list.
          </p>

          <figure className="prediction-photo-main">
            <img src={highlightedImage} alt={getSpeciesImageAlt(highlightedSpecies)} />
            <figcaption>
              <strong>{highlightedSpecies}</strong>
              {lastPrediction ? (
                <span>
                  {lastPrediction.risk_level} risk at {(lastPrediction.confidence * 100).toFixed(1)}% confidence
                </span>
              ) : (
                <span>Choose a species and run prediction to update spotlight.</span>
              )}
            </figcaption>
          </figure>

          <div className="prediction-photo-grid" aria-label="Species prediction photo gallery">
            {SPECIES_OPTIONS.map((speciesOption) => (
              <button
                key={speciesOption}
                type="button"
                className={`prediction-photo-card${speciesOption === highlightedSpecies ? ' active' : ''}`}
                onClick={() => setSpecies(speciesOption)}
              >
                <img
                  src={SPECIES_IMAGE_MAP[speciesOption] || danausPlexippusImage}
                  alt={getSpeciesImageAlt(speciesOption)}
                />
                <span>{speciesOption}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="panel gbif-panel">
        <div className="gbif-header-row">
          <div>
            <h2>Global Insect Records (GBIF)</h2>
            <p>
              Worldwide insect occurrence feed with trend intelligence and biodiversity watch metrics.
            </p>
          </div>
          <button type="button" onClick={loadGlobalRecords} disabled={loadingGlobalRecords}>
            {loadingGlobalRecords ? 'Refreshing Feed...' : 'Refresh Global Feed'}
          </button>
        </div>

        {normalizedGlobal ? (
          <>
            {globalRecordsError && <p className="gbif-warning">Showing last successful global feed. Refresh failed: {globalRecordsError}</p>}
            <div className="gbif-metric-grid">
              <article className="gbif-metric-card">
                <h3>Sampled Records (Live)</h3>
                <p>{normalizedGlobal.totalSampledRecords}</p>
              </article>
              <article className="gbif-metric-card">
                <h3>Sampled Unique Species</h3>
                <p>{normalizedGlobal.uniqueSpecies}</p>
              </article>
              <article className="gbif-metric-card">
                <h3>Mapped Country Coverage</h3>
                <p>{normalizedGlobal.countryCoverage}</p>
              </article>
              <article className="gbif-metric-card">
                <h3>GBIF Feed</h3>
                <p>{normalizedGlobal.isFallback ? 'Fallback Snapshot' : 'Live Records'}</p>
              </article>
              <article className="gbif-metric-card">
                <h3>NASA Avg Temp</h3>
                <p>{normalizedGlobal.nasa.avgTemperatureCelsius.toFixed(1)} C</p>
              </article>
              <article className="gbif-metric-card">
                <h3>NASA Vegetation</h3>
                <p>{normalizedGlobal.nasa.vegetationIndex.toFixed(2)}</p>
              </article>
            </div>

            <p className="gbif-footnote">
              Metric cards use sampled live records (limit {normalizedGlobal.queryLimit || 0}). Trend chart uses GBIF year
              facet distribution for 1990-2026 with a server-generated forecast extension.
              Country hotspot counts use GBIF country facet totals.
              {observedSeries.excludedPartialYear ? ` Observed ${observedSeries.excludedPartialYear} is shown as partial because the year is still in progress.` : ''}
            </p>

            <div className="gbif-main-grid">
              <article className="gbif-chart-card">
                <h3>Global Occurrence Trend (GBIF Year Facet)</h3>
                <div className="gbif-chart-wrap">
                  <ResponsiveContainer width="100%" height="100%" debounce={120}>
                    <LineChart data={trendChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#d8e2de" />
                      <XAxis dataKey="year" ticks={trendChartTicks} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="count" stroke="#1f7a5a" strokeWidth={2.5} dot={false} name="Observed Records" connectNulls={false} />
                      <Line
                        type="linear"
                        dataKey="partialCount"
                        stroke="#1f7a5a"
                        strokeWidth={2}
                        strokeDasharray="2 3"
                        dot={{ r: 3 }}
                        name="Observed (Partial Year)"
                        connectNulls={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="forecastCount"
                        stroke="#7aa57f"
                        strokeWidth={2.5}
                        strokeDasharray="6 4"
                        dot={false}
                        name="Forecast Records"
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </article>

              <article className="gbif-chart-card">
                <h3>Top Species by Records</h3>
                <div className="gbif-chart-wrap">
                  <ResponsiveContainer width="100%" height="100%" debounce={120}>
                    <BarChart data={normalizedGlobal.topSpecies.slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#d8e2de" />
                      <XAxis dataKey="species" hide />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#2571a4" radius={[5, 5, 0, 0]} name="Species Records" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <ul className="gbif-species-list">
                  {normalizedGlobal.topSpecies.slice(0, 5).map((item) => (
                    <li key={item.species}>
                      <span>{item.species}</span>
                      <strong>{item.count}</strong>
                    </li>
                  ))}
                </ul>
              </article>
            </div>

            <div className="gbif-sub-grid">
              <article className="gbif-sub-card">
                <h3>Biodiversity Hotspot Map (Countries)</h3>
                <ul className="gbif-country-list">
                  {normalizedGlobal.topCountries.slice(0, 6).map((item) => (
                    <li key={item.country}>
                      <span>{item.country}</span>
                      <strong>{formatCompactCount(item.count)}</strong>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="gbif-sub-card">
                <h3>NASA Environmental Stats</h3>
                <ul className="gbif-basis-list">
                  <li>
                    <span>Avg Humidity</span>
                    <strong>{normalizedGlobal.nasa.avgHumidityPercent.toFixed(1)}%</strong>
                  </li>
                  <li>
                    <span>Total Precipitation</span>
                    <strong>{normalizedGlobal.nasa.totalPrecipitationMm.toFixed(1)} mm</strong>
                  </li>
                  <li>
                    <span>Soil Moisture Index</span>
                    <strong>{normalizedGlobal.nasa.soilMoistureIndex.toFixed(3)}</strong>
                  </li>
                  <li>
                    <span>Sampled Regions</span>
                    <strong>{normalizedGlobal.nasa.sampledRegions}</strong>
                  </li>
                </ul>
              </article>

              <article className="gbif-sub-card">
                <h3>Recent Alerts</h3>
                <ul className="gbif-alert-list">
                  {normalizedGlobal.recentAlerts.map((alert) => (
                    <li key={alert}>{alert}</li>
                  ))}
                </ul>
              </article>
            </div>
          </>
        ) : (
          <p className="analytics-summary">
            {loadingGlobalRecords
              ? 'Loading global records...'
              : globalRecordsError || 'Unable to load global records right now.'}
          </p>
        )}
      </section>

      {error && <p className="form-error">{error}</p>}
    </section>
  );
};

export default DashboardPage;
